import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '../api'

export interface InventoryCategory {
  id: string
  name: string
  type: 'location' | 'item_type'
  parent_id: string | null
  sort_order: number
}

export interface InventoryItem {
  id: string
  name: string
  description: string | null
  make: string | null
  model: string | null
  serial_number: string | null
  purchase_location: string | null
  purchase_date: string | null
  purchase_price: number | null
  warranty_expires: string | null
  photo_url: string | null
  photo_key: string | null
  location_id: string | null
  category_id: string | null
  condition: 'excellent' | 'good' | 'fair' | 'poor' | 'needs_replacement' | null
  notes: string | null
  manual_url: string | null
  created_at: string
  updated_at: string
  created_by: string | null
  location_name?: string
  category_name?: string
}

export interface CreateItemRequest {
  name: string
  description?: string
  make?: string
  model?: string
  serial_number?: string
  purchase_location?: string
  purchase_date?: string
  purchase_price?: number
  warranty_expires?: string
  photo_key?: string
  location_id?: string
  category_id?: string
  condition?: 'excellent' | 'good' | 'fair' | 'poor' | 'needs_replacement'
  notes?: string
  manual_url?: string
}

export const useInventoryStore = defineStore('inventory', () => {
  const items = ref<InventoryItem[]>([])
  const categories = ref<InventoryCategory[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const locations = computed(() =>
    categories.value.filter(c => c.type === 'location')
  )

  const itemTypes = computed(() =>
    categories.value.filter(c => c.type === 'item_type')
  )

  const itemsByLocation = computed(() => {
    const grouped: Record<string, InventoryItem[]> = {}
    for (const item of items.value) {
      const key = item.location_id || 'unassigned'
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(item)
    }
    return grouped
  })

  const needsAttention = computed(() =>
    items.value.filter(i => i.condition === 'poor' || i.condition === 'needs_replacement')
  )

  async function fetchCategories() {
    try {
      const response = await api.get('/api/inventory/categories')
      categories.value = response.categories
    } catch (err) {
      console.error('Failed to fetch categories:', err)
    }
  }

  async function createCategory(data: { name: string; type: 'location' | 'item_type'; parent_id?: string; sort_order?: number }) {
    const response = await api.post('/api/inventory/categories', data)
    await fetchCategories()
    return response.category
  }

  async function fetchItems(filters?: { location?: string; category?: string; condition?: string; search?: string }) {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams()
      if (filters?.location) params.set('location', filters.location)
      if (filters?.category) params.set('category', filters.category)
      if (filters?.condition) params.set('condition', filters.condition)
      if (filters?.search) params.set('search', filters.search)

      const query = params.toString() ? `?${params}` : ''
      const response = await api.get(`/api/inventory${query}`)
      items.value = response.items
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch inventory'
    } finally {
      loading.value = false
    }
  }

  async function fetchItem(id: string): Promise<InventoryItem | null> {
    try {
      const response = await api.get(`/api/inventory/${id}`)
      return response.item
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch item'
      return null
    }
  }

  async function createItem(data: CreateItemRequest) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/api/inventory', data)
      await fetchItems()
      return response.item
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create item'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateItem(id: string, data: Partial<CreateItemRequest>) {
    loading.value = true
    error.value = null

    try {
      await api.patch(`/api/inventory/${id}`, data)
      await fetchItems()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update item'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function deleteItem(id: string) {
    loading.value = true
    error.value = null

    try {
      await api.delete(`/api/inventory/${id}`)
      await fetchItems()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete item'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function getUploadUrl(filename: string, contentType: string) {
    const response = await api.post('/api/inventory/upload-url', { filename, contentType })
    return response
  }

  async function uploadPhoto(uploadUrl: string, file: File): Promise<string> {
    const API_BASE = import.meta.env.VITE_API_URL || ''
    const token = localStorage.getItem('session_token')

    const response = await fetch(`${API_BASE}${uploadUrl}`, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        ...(token ? { 'X-Session-Token': token } : {}),
      },
      body: file,
    })

    if (!response.ok) {
      throw new Error('Failed to upload photo')
    }

    const data = await response.json()
    return data.key
  }

  return {
    items,
    categories,
    locations,
    itemTypes,
    itemsByLocation,
    needsAttention,
    loading,
    error,
    fetchCategories,
    createCategory,
    fetchItems,
    fetchItem,
    createItem,
    updateItem,
    deleteItem,
    getUploadUrl,
    uploadPhoto,
  }
})
